/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import _ from 'lodash';
import Radio from 'backbone.radio';
import Backbone from 'backbone';

import AttachmentsCollectionView from '@/views/AttachmentsCollectionView';
import DocumentDownloadTypes from '@/enums/DocumentDownloadTypes';
import DocumentTypes from '@/enums/DocumentTypes';
import OrderFileUploadTmpl from '@/fileupload/OrderFileUploadTmpl.hbs';
import ValidFileTypeUtil from '@/util/ValidFileTypeUtil';

import AdHocAttachmentsView from '../AdHocAttachmentsView';
import GetOrderAdHocAttachments from '../../../order/services/GetOrderAdHocAttachments';

import tmplAttachmentCardItemView from './AttachmentCardItemView.hbs';
import OrderSummaryAttachmentView from '../OrderSummaryAttachmentView/OrderSummaryAttachmentView';

const AttachmentCardItemView = View.extend({
  template: tmplAttachmentCardItemView,

  className: 'card-item-view',

  ui: {
    attachments: '.attachments',
    adHocAttachments: '.adhoc-attachments',
    orderSummaryAttachment: '.order-summary-attachment'
  },

  regions: {
    attachments: '@ui.attachments',
    adHocAttachments: '@ui.adHocAttachments',
    orderSummaryAttachment: '@ui.orderSummaryAttachment'
  },

  initialize(options) {
    const isReadOnly = this.model.get('isReadOnly');
    const attachments = this.model.get('attachments');

    const documentTypes = this.model.get('documentTypes').filter(({ documentTypeId }) => {
      switch(documentTypeId) {
        // CHESS and 7600A labels should only appear if an attachment already exists
        case DocumentTypes.CHESS:
        case DocumentTypes['7600A']:
          return attachments.find(attachment => attachment.documentTypeId === documentTypeId);
        // communication and note attachments should never display with other attachments
        case DocumentTypes.COMMUNICATION:
        case DocumentTypes.NOTE:
          return false;
        default:
          return true;
      }
    });

    const documentMapping = this.mapDocs(documentTypes, attachments, options, isReadOnly);
    this.model.set('documentMapping', documentMapping);

    this.listenTo(Radio.channel('order'), 'file:added', (coll) => {
      this.mapDocs(documentTypes, attachments, options, isReadOnly);
    });
  },

  mapDocs(documentTypes, attachments, options, isReadOnly) {
    const documentMapping = _.map(documentTypes, (documentType) => {
      const { documentTypeDescription, documentTypeId, attachmentId, defaultFileName } = documentType;
      const attachment = _.find(attachments, (attachment) => attachment.documentTypeId === documentTypeId) || null;

      return {
        label: {
          text: documentTypeDescription,
          fileName: documentType.defaultFileName,
          isDocument: documentType.attachmentId, // indicates whether the label is a document (available for download)
          downloadType: DocumentDownloadTypes.TEMPLATE, // used in the download bpm to signify that we need to use the LU_DocumentType to get the cs id
          attachmentId
        },
        attachment: {
          isReadOnly: true,
          attachmentName: attachment?.fileName,
          attachmentId: attachment?.id,
          fileUploadTmpl: OrderFileUploadTmpl,
          pathToFileId: 'html.head.script.Envelope.Body.UploadOrderAttachmentResponse.AttachmentID.__text',
          defaultFileName,
          validator: ValidFileTypeUtil,
          metadata: {
            attachmentId: attachment?.id,
            fileName: attachment?.fileName,
            orderId: attachment?.objectId || options?.model?.get('orderID'),
            documentTypeId
          }
        },
        isReadOnly
      };
    });
    return documentMapping;
  },

  onRender() {
    this.showAttachmentsView();
    this.showAdHocAttachmentsView();
  },

  showAttachmentsView() {
    const collection = new Backbone.Collection(this.model.get('documentMapping'));
    this.attachmentsCollectionView = new AttachmentsCollectionView({ collection });
    this.showChildView('attachments', this.attachmentsCollectionView);
  },

  async showAdHocAttachmentsView() {
    const orderId = this.model.get('orderID');
    const attachments = (await GetOrderAdHocAttachments(orderId)) || [];
    const editable = this.model.get('isAdHocEditable');
    const adHocAttachmentsModel = new Backbone.Model({
      attachments,
      editable,
      orderId
    });
    this.showChildView('adHocAttachments', new AdHocAttachmentsView({ model: adHocAttachmentsModel }));
  },

  async showOrderSummaryAttachmentView(){
    const orderSummaryAttachmentModel = new Backbone.Model({
      attachment
    })
    this.showChildView('orderSummaryAttachment', new OrderSummaryAttachmentView({ model: orderSummaryAttachmentModel}))
  }
});

export default AttachmentCardItemView;
